import { Drawer, type DrawerProps } from "@mui/material";
import { useMemo } from "react";

type PersistentDrawerProps = {
  open?: boolean;
  children: React.ReactNode;
  shouldNotTrackHeight?: boolean;
  noShadow?: boolean;
  noBorder?: boolean;
  borderRadius?: string;
  backgroundColor?: string;
  padding?: string;
  heightVariableName?: `--${string}`;
};

export function PersistentDrawer({
  children,
  open = true,
  shouldNotTrackHeight = false,
  noShadow = false,
  noBorder = false,
  borderRadius = "var(--radius-4xl) var(--radius-4xl) 0 0",
  backgroundColor = "var(--color-white)",
  padding = "calc(var(--spacing)*4) calc(var(--spacing)*4) var(--drawer-bottom-padding)",
  heightVariableName = "--drawer-height",
}: PersistentDrawerProps) {
  const slotProps = useMemo<DrawerProps["slotProps"]>(() => {
    return {
      paper: {
        style: {
          height: "auto",
          borderRadius,
          background: backgroundColor,
          padding,
          border: noBorder ? "none" : undefined,
          boxShadow: noShadow
            ? "none"
            : "0 -1px 12px 0 rgba(0, 0, 0, 0.08), 0 -4px 12px 0 rgba(0, 0, 0, 0.04)",
        },
        ref: shouldNotTrackHeight
          ? undefined
          : (node: HTMLDivElement) => {
              const observer = new ResizeObserver(([entry]) => {
                document.body.style.setProperty(
                  heightVariableName,
                  `${entry.target.getBoundingClientRect().height}px`,
                );
              });

              observer.observe(node);

              return () => {
                observer.disconnect();
                document.body.style.setProperty(heightVariableName, "0px");
              };
            },
      },
    };
  }, [
    borderRadius,
    noBorder,
    noShadow,
    shouldNotTrackHeight,
    heightVariableName,
    backgroundColor,
    padding,
  ]);

  return (
    <Drawer
      slotProps={slotProps}
      anchor="bottom"
      variant="persistent"
      open={open}
    >
      <div className="w-full">{children}</div>
    </Drawer>
  );
}
